import React, { useEffect, useState , useCallback} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SortingRule } from 'react-table';
import { Col, Row } from 'reactstrap';
import Toast from "../../helpers/Toast";
import ButtonComponent from '../ButtonComponent';
import InputComponent from '../InputComponent';
import TableComponent from '../TableComponent';
import { PartQuotationListDTO } from '../../models/Quotation/PartQuotationListDTO';
import SearchSelectComponent, { SelectItemsProps } from "../../components/SearchSelectComponent";
import { PartService } from '../../services/PartService';
import { SearchPartByName } from '../../models/Parts/SearchPartByName';
import { ClientQuotationService } from '../../services/ClientQuotationService';
import { UpdateClientQuotationDTO } from '../../models/Quotation/UpdateClientQuotationDTO';
import { PartsAndServicesQuotationDTO } from '../../models/Quotation/PartsAndServicesQuotationDTO';
import { Utilities } from '../../helpers/Utilities';
export type PartsTableComponentType = {
    initialParts: PartQuotationListDTO[] | [];
    reloadParts: boolean;
    goToPage: number;
    currentPage: number;
    isComplete: boolean;
    onPartsChange: (parts: PartQuotationListDTO[]) => void;
    quotationType : string;
    quotationId?: number;
    clientId?: string; 
    description?: string; 
}

const QuotationPartsTable = ({
    initialParts, 
    reloadParts, 
    goToPage, 
    currentPage, 
    isComplete, 
    onPartsChange, 
    quotationType,  
    quotationId,
    clientId,
    description
}: PartsTableComponentType) => {

    const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
    const [parts, setParts] = useState<PartQuotationListDTO[]>(initialParts);
    const [totalPages, setTotalPages] = useState<number>(0);
    const pageSizes: number[] = [5, 15, 30];
    const intl = useIntl()
    const [quantity, setQuantity] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(5);
    const [initialSortBy] = useState<SortingRule<object>[]>([]);
    const [sellingPrice, setSellingPrice] = useState<number>(0);
    let [code, setCode] = useState<string>("NaN");
    const [name, setName] = useState<string>("");
    const [costPrice, setCostPrice] = useState<number>(0);
    const partService = new PartService();
    const [selectedPart, setSelectedPart] = useState<SelectItemsProps | null>(null);
    const [inputpart, setInputPart] = useState<string>("");
    const clientQuotationService = new ClientQuotationService();

    const searchPart = async (name: string): Promise<SelectItemsProps[]> => {
        const searchParams: SearchPartByName = { name };
        const response = await partService.SearchPartByName(searchParams);

        if (!response.success || response.obj === null) {
            Toast.Show("error", response.message);
            return [];
        }

        return response.obj.map((element) => ({
            value: element.erpId,
            label: element.name,
        }));
    };
 
    const fetchPartDetails = async (erpId: string) => {
        const response = await partService.GetByIdErp(erpId);

        if (response.success && response.obj) {
            const part = response.obj;
            setCode(part.erpId);
            setName(part.name);
            setSellingPrice(part.sellingPrice ?? 0);
            setCostPrice(part.costPrice ?? 0);
        } else {
            Toast.Show("error", response.message);
        }
    };


    useEffect(() => {
        setParts(initialParts);
        onPartsChange(initialParts);
    }, [initialParts]);

    useEffect(() => {
        setParts(parts);
    }, [parts]);


    useEffect(() => {
        RemoveFilters();
    }, [parts]);

    const fetchData = async () => {
        setParts(parts);
    }

    const RemoveFilters = () => {
        setCode("");
        setName("");
        setQuantity(0);
        setSellingPrice(0);
        setCostPrice(0);
    }
    const handleQuantityChange = (index: number, newQuantity: number) => {
        if (!isNaN(newQuantity) && newQuantity > 0) {
            setParts(prevParts => {
                const updatedParts = [...prevParts];
                updatedParts[index] = {
                    ...updatedParts[index],
                    quantity: newQuantity,
                    totalValue: newQuantity * updatedParts[index].sellingPrice,
                };
                onPartsChange(updatedParts);
                return updatedParts;
            });
        }
    };
    const handleCostPriceChange = (index: number, newCostPrice: number) => {
        if (!isNaN(newCostPrice) && newCostPrice >= 0) {
            setParts(prevParts => {
                const updatedParts = [...prevParts];
                updatedParts[index] = {
                    ...updatedParts[index],
                    costPrice: newCostPrice,
                };
                onPartsChange(updatedParts);
                return updatedParts;
            });
        }
    };

    const handleSellingPriceChange = (index: number, newSellingPrice: number) => {
        if (!isNaN(newSellingPrice) && newSellingPrice >= 0) {
            setParts(prevParts => {
                const updatedParts = [...prevParts];
                const currentPart = updatedParts[index];
                updatedParts[index] = {
                    ...currentPart,
                    sellingPrice: newSellingPrice,
                    totalValue: currentPart.quantity * newSellingPrice,
                };
                onPartsChange(updatedParts);
                return updatedParts;
            });
        }
    };

    const deletePart = (index: number) => {
        setParts(prevParts => {
            const updatedParts = prevParts.filter((_, i) => i !== index);
            onPartsChange(updatedParts); 
            return updatedParts; 
        });
    };

    const savePart = useCallback(async (index: number) => {
        if (!quotationId) {
            Toast.Show("error",intl.formatMessage({ id: "Screens.Quotation.Parts.Info.QuotationDoesNotExist" }));
            return;
        }

        if (!clientId && quotationType === 'ClientQuotation') {
            Toast.Show("error", intl.formatMessage({ id: "Screens.Quotation.Parts.Info.ClientNotDefined" }));
            return;
        }

        try {
            setIsLoadingTable(true);

            const quantityInput = document.querySelector<HTMLInputElement>(`.new-quantity-input[data-row-index="${index}"]`);
            const costPriceInput = document.querySelector<HTMLInputElement>(`.cost-price-input[data-row-index="${index}"]`);
            const sellingPriceInput = document.querySelector<HTMLInputElement>(`.selling-price-input[data-row-index="${index}"]`);

            if (!quantityInput || !costPriceInput || (quotationType === 'ClientQuotation' && !sellingPriceInput)) {
                Toast.Show("error", intl.formatMessage({ id: "Screens.ClientQuotation.Parts.LoadError" }));
                return;
            }

            const newQuantity = parseInt(quantityInput.value);
            const newCostPrice = parseFloat(costPriceInput.value);
            const newSellingPrice = quotationType === 'ClientQuotation' 
                ? parseFloat(sellingPriceInput!.value) 
                : parts[index].sellingPrice;
            const newTotal = Utilities.calculateTotalPrice(newQuantity, newSellingPrice);

            

            setParts(prevParts => {
                const updatedParts = [...prevParts];
                updatedParts[index] = {
                    ...updatedParts[index],
                    quantity: newQuantity,
                    costPrice: newCostPrice,
                    sellingPrice: newSellingPrice,
                    totalValue: newTotal,
                };

                const newTotalValue = updatedParts.reduce((sum, part) => 
                    sum + Utilities.calculateTotalPrice(part.quantity, part.sellingPrice), 0
                );
                
                const newCostPriceTotal = updatedParts.reduce((sum, part) => 
                    sum + Utilities.calculateTotalPrice(part.quantity, part.costPrice), 0
                );

                const partsAndServicesQuotationDTO: PartsAndServicesQuotationDTO[] = updatedParts.map(part => ({
                    partName: part.name,
                    partId: String(part.code),
                    quantity: Number(part.quantity),
                    sellingPrice: Number(part.sellingPrice),
                    costPrice: Number(part.costPrice),
                    totalSellingPrice: Utilities.calculateTotalPrice(part.quantity, part.sellingPrice)
                }));

                const clientIdNumber = clientId ? parseInt(clientId) : 0; 
                const updateQuotationDTO: UpdateClientQuotationDTO = {
                    quotationId: quotationId,
                    clientId: clientIdNumber,
                    description: description || "",
                    partsAndServicesQuotationDTO: partsAndServicesQuotationDTO,
                    sellingPrice: newTotalValue,
                    costPrice: newCostPriceTotal
                };      

                

                (async () => {
                    try {
                        const response = await clientQuotationService.Update(updateQuotationDTO, quotationId);

                        if (!response?.success) {
                            Toast.Show("error", response?.message || intl.formatMessage({ id: "Screens.ClientQuotation.Parts.UpdateError" }));
                        } else {
                            
                            Toast.Show("success",intl.formatMessage({ id: "Screens.ClientQuotation.Parts.UpdateSuccess" }));
                            
                            onPartsChange(updatedParts);
                        }
                    } catch (error) {
                        Toast.Show("error", intl.formatMessage({ id: "Screens.ClientQuotation.Parts.UpdateError" }));
                    } finally {
                        setIsLoadingTable(false);
                    }
                })();

                return updatedParts;
            });
        } catch (error) {
            Toast.Show("error", intl.formatMessage({ id: "Screens.ClientQuotation.Parts.UpdateError" }));
            setIsLoadingTable(false);
        }
    }, [quotationId, clientId, description, clientQuotationService, onPartsChange, parts, quotationType]);

    const AddToTable = async () => {
        if (code === "NaN" || name === "" || quantity === 0) {
            Toast.Show('error', intl.formatMessage({ id: "Screens.Quotation.Parts.Info.FillAllFields" }));
            return;
        }

        if (parts.some(part => part.code === String(code))) {
            Toast.Show('error', intl.formatMessage({ id: "Screens.Quotation.Parts.Info.CodeAlreadyExists" }));
            return;
        }

        if (!quotationId) {
            Toast.Show("error", "Proposta ainda não foi criada, não é possível adicionar");
            return;
        }

        if (!clientId && quotationType === 'ClientQuotation') {
            Toast.Show("error", intl.formatMessage({ id: "Screens.ClientQuotation.Parts.Info.ClientNotDefined" }));
            return;
        }

        const newPart: PartQuotationListDTO = {
            code: String(code),
            name: name,
            quantity: quantity,
            costPrice: costPrice,
            sellingPrice: sellingPrice,
            totalValue: quantity * sellingPrice,
        };

        const updatedParts = [...parts, newPart];

        const newTotalValue = updatedParts.reduce((sum, part) =>
            sum + Utilities.calculateTotalPrice(part.quantity, part.sellingPrice), 0
        );

        const newCostPrice = updatedParts.reduce((sum, part) =>
            sum + Utilities.calculateTotalPrice(part.quantity, part.costPrice), 0
        );

        const partsAndServicesQuotationDTO: PartsAndServicesQuotationDTO[] = updatedParts.map(part => ({
            partName: part.name,
            partId: String(part.code),
            quantity: Number(part.quantity),
            sellingPrice: Number(part.sellingPrice),
            costPrice: Number(part.costPrice),
            totalSellingPrice: Utilities.calculateTotalPrice(part.quantity, part.sellingPrice)
        }));

        const updateQuotationDTO: UpdateClientQuotationDTO = {
            quotationId: quotationId,
            clientId: parseInt(clientId!),
            description: description || "",
            partsAndServicesQuotationDTO: partsAndServicesQuotationDTO,
            sellingPrice: newTotalValue,
            costPrice: newCostPrice
        };

        setIsLoadingTable(true);

        try {
            const response = await clientQuotationService.Update(updateQuotationDTO, quotationId);

            if (!response.success) {
                Toast.Show("error", response.message || "Erro ao guardar a nova peça");
                return;
            }

            setParts(updatedParts);
            onPartsChange(updatedParts);
            Toast.Show("success", intl.formatMessage({ id: "Screens.ClientQuotation.Parts.AddSuccess" }));
            RemoveFilters();
        } catch (error) {
            Toast.Show("error", intl.formatMessage({ id: "Screens.ClientQuotation.Parts.AddError" }));
        } finally {
            setIsLoadingTable(false);
        }
        RemoveFilters();
    };

    const columns = React.useMemo(() => {
        if (quotationType === 'SupplierQuotation') {
            return [
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Code" />,
                    accessor: "code",
                    width: 15,
                    Cell: ({ cell }: { cell: { value: number } }) => {
                        const partId = cell.value;
                        return (
                            <div>
                                {partId === 0 ? (
                                    <FormattedMessage id="Screens.Quotation.Parts.Message.NoPart" />
                                ) : (
                                    partId
                                )}
                            </div>
                        );
                    },
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Name" />,
                    accessor: "name",
                    width: 25,
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Quantity" />,
                    accessor: "quantity",
                    width: 15,
                    Cell: ({ row, value }: { row: { index: number }; value: number }) => {
                        if (isComplete) {
                            return <span>{value}</span>;
                        }
                        return (
                            <input
                                type="number"
                                min={1}
                                value={value}
                                data-row-index={row.index}
                                className="new-quantity-input"
                                onChange={(e) =>handleQuantityChange(row.index, parseInt(e.target.value))}
                                style={{ width: '80px' }}
                            />
                        );
                    },
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.CostPrice" />,
                    accessor: "costPrice",
                    width: 15,
                    Cell: ({ row, value }: { row: { index: number }; value: number }) => {
                        if (isComplete) {
                            return <span>{value.toFixed(2)}</span>;
                        }
                        return (
                            <input
                                type="number"
                                min={0}
                                step={0.01}
                                data-row-index={row.index}
                                className="cost-price-input"
                                value={value}
                                onChange={(e) => handleCostPriceChange(row.index, parseFloat(e.target.value))}
                                style={{ width: '80px' }}
                            />
                        );
                    },
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Actions"/>,
                    accessor: "actions",
                    width: 15,
                    Cell: ({ row: { index } }: { row: { index: number } }) => (
                        <ButtonComponent
                            style={{ margin: 'auto', width: '40%'}}
                            text= {intl.formatMessage({ id: "Screens.Quotation.Parts.Remove"})}
                            onClick={() => deletePart(index)}
                            disabled={isComplete}
                        />
                    ),
                },
                {
                    Header: "Guardar",
                    accessor: "save",
                    width: 15,
                    Cell: ({ row: { index } }: { row: { index: number } }) => (
                        <ButtonComponent
                            style={{ margin: 'auto', width: '40%'}}
                            text={intl.formatMessage({ id: "Screens.ClientQuotation.Button.Save"})}
                            onClick={() => savePart(index)}
                            disabled={isComplete || isLoadingTable}
                            loading={isLoadingTable}
                        />
                    ),
                },
            ];
        }
    
        if (quotationType === 'ClientQuotation') {
            return [
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Code" />,
                    accessor: "code",
                    width: 12,
                    Cell: ({ cell }: { cell: { value: number } }) => {
                        const partId = cell.value;
                        return (
                            <div>
                                {partId === 0 ? (
                                    <FormattedMessage id="Screens.Quotation.Parts.Message.NoPart" />
                                ) : (
                                    partId
                                )}
                            </div>
                        );
                    },
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Name" />,
                    accessor: "name",
                    width: 20,
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Quantity" />,
                    accessor: "quantity",
                    width: 12,
                    Cell: ({ row, value }: { row: { index: number }; value: number }) => {
                        if (isComplete) {
                            return <span>{value}</span>;
                        }
                        return (
                            <input
                                type="number"
                                min={1}
                                value={value}
                                data-row-index={row.index}
                                className="new-quantity-input"
                                onChange={(e) => {handleQuantityChange(row.index, parseInt(e.target.value))}}
                                style={{ width: '80px' }}
                            />
                        );
                    },
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.CostPrice" />,
                    accessor: "costPrice",
                    width: 12,
                    Cell: ({ row, value }: { row: { index: number }; value: number }) => {
                        if (isComplete) {
                            return <span>{value.toFixed(2)}</span>;
                        }
                        return (
                            <input
                                type="number"
                                min={0}
                                step={0.01}
                                data-row-index={row.index}
                                className="cost-price-input"
                                value={value}
                                onChange={(e) => {handleCostPriceChange(row.index, parseFloat(e.target.value))}}
                                style={{ width: '80px' }}
                            />
                        );
                    },
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.SellingPrice" />,
                    accessor: "sellingPrice",
                    width: 12,
                    Cell: ({ row, value }: { row: { index: number }; value: number }) => {
                        if (isComplete) {
                            return <span>{value}</span>;
                        }
                        return (
                            <input
                                type="number"
                                min={0}
                                step="0.01"
                                value={value}
                                data-row-index={row.index}
                                className="selling-price-input"
                                onChange={(e) => {handleSellingPriceChange(row.index, parseFloat(e.target.value))}}
                                style={{ width: '80px' }}
                            />
                        );
                    },
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.TotalValue" />,
                    accessor: "totalValue",
                    width: 12,
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Actions"/>,
                    accessor: "actions",
                    width: 10,
                    Cell: ({ row: { index } }: { row: { index: number } }) => (
                        <ButtonComponent
                            style={{ margin: 'auto', width: '40%'}}
                            text= {intl.formatMessage({ id: "Screens.Quotation.Parts.Remove"})}
                            onClick={() => deletePart(index)}
                            disabled={isComplete}
                        />
                    ),
                },
                {
                    Header: <FormattedMessage id="Screens.ClientQuotation.Button.Save" />,
                    accessor: "save",
                    width: 10,
                    Cell: ({ row: { index } }: { row: { index: number } }) => (
                        <ButtonComponent
                            style={{ margin: 'auto', width: '40%'}}
                            text={intl.formatMessage({ id: "Screens.ClientQuotation.Button.Save" })}
                            onClick={() => savePart(index)}
                            disabled={isComplete || isLoadingTable}
                            loading={isLoadingTable}
                        />
                    ),
                },
            ];
        }
        return [];
    }, [quotationType, isComplete, isLoadingTable]);

    useEffect(() => {
        fetchData();
    }, [reloadParts]);

    return (
        <div>
            <Row xl={12} lg={12} md={12} sm={12} xs={12}>
            <Col xl={12} lg={12} md={12} sm={12} xs={12}>
                    <Row xl={12} lg={12} md={12} sm={12} xs={12}>
                    <SearchSelectComponent xl={1.5} lg={2} md={4} sm={8} xs={12}
                        label={intl.formatMessage({ id: "Pesquisar peças" })}
                        required={true}
                        loadOptions={searchPart} // Busca no dropdown
                        onChange={(e: SelectItemsProps) => {
                            setSelectedPart(e);
                            setInputPart(e.value);
                            fetchPartDetails(e.value); // Busca detalhes ao selecionar
                        }}
                        value={selectedPart}
                        style={{ width: "100%" }}
                        />
                    <InputComponent xl={1.5} lg={2} md={4} sm={8} xs={12}
                            style={{ width: "100%" }}
                            type={"text"}
                            value={code}
                            label={intl.formatMessage({ id: "Screens.Quotation.Parts.Label.Code" })}
                            readOnly={true}
                        />
                        
                        <InputComponent xl={1.5} lg={2} md={4} sm={8} xs={12}
                            style={{ width: "100%" }}
                            type={"text"}
                            value={name}
                            label={intl.formatMessage({ id: "Screens.Quotation.Parts.Label.Name" })}
                            readOnly={true}
                        />
                        <InputComponent xl={1.5} lg={2} md={4} sm={8} xs={12}
                            style={{ width: "100%" }}
                            type={"number"}
                            value={quantity}
                            label={intl.formatMessage({ id: "Screens.Quotation.Parts.Label.Quantity" })}
                            required={true}
                            readOnly={isComplete}
                            onChange={(e: number) => setQuantity(e)}
                        />
                        <InputComponent xl={1.5} lg={2} md={4} sm={8} xs={12}
                            style={{ width: "100%" }}
                            type={"number"}
                            value={costPrice}
                            label={intl.formatMessage({ id: "Screens.Quotation.Parts.Label.CostPrice" })}
                            readOnly={isComplete}
                            onChange={(e: number) => setCostPrice(e)}
                        />
                        {quotationType === 'ClientQuotation' && (
                            <InputComponent xl={1.5} lg={2} md={4} sm={8} xs={12}
                                style={{ width: "100%" }}
                                type={"number"}
                                value={sellingPrice}
                                label={intl.formatMessage({ id: "Screens.Quotation.Parts.Label.SellingPrice" })}
                                readOnly={isComplete}
                                onChange={(e: number) => setSellingPrice(e)}
                            />
                        )}
                    </Row>
                </Col>
                <Row>
                    <Col xl={1} lg={2} md={3} sm={4} xs={6}>
                        <ButtonComponent
                            style={{ marginTop: '10%', width: '100%'}}
                            text={intl.formatMessage({ id: "Screens.Assistances.Parts.Label.ClearBoxes" })}
                            onClick={() => RemoveFilters()}
                            disabled={isComplete}
                        />
                    </Col>
                    <Col xl={1} lg={2} md={3} sm={4} xs={6}>
                        <ButtonComponent
                            style={{ marginTop: '10%',width: '100%'}}
                            text={intl.formatMessage({ id: "Screens.Quotation.Parts.Label.Add" })}
                            onClick={() => {
                                AddToTable();
                            }}
                            disabled={isComplete}
                        />
                    </Col>
                </Row>
                <Row style={{ margin: "1rem 0px" }}>
                    <TableComponent
                        goToPage={goToPage}
                        loading={isLoadingTable}
                        pageCount={totalPages}
                        columns={columns.map(column => ({
                            ...column,
                            accessor: String(column.accessor) 
                        }))}
                        manualPagination={true}
                        fetchData={fetchData}
                        pageSizes={pageSizes}
                        initialPage={currentPage}
                        initialPageSize={pageSize}
                        initialSortBy={initialSortBy}
                        data={parts}
                        dontOrderLastCollumn={true}
                        dontOrderFirstCollumn={false}
                    />
                </Row>
            </Row>
        </div>
    )
}

export default QuotationPartsTable